import { useCallback, useEffect, useState } from "react";

import { send } from "../api/auth";
import { console_ } from "../api/console";
import { StatusChip } from "../design/StatusChip";
import { useT } from "../i18n/LocaleProvider";
import { useSession } from "../session/SessionProvider";
import { clock } from "../system/format";

// ---------------------------------------------------------------------------
// D12. HEADCOUNT PER ROLE AND ZONE, AND THE ROLL CALL THAT FREEZES IT (0291).
//
// The per-person roll call is RollCall.tsx, one post at a time. This is the count
// across posts, as app.roll_call_tally gives it, and the button that saves it under
// the caller's name. It shows no person: that is the point of the slice.
// ---------------------------------------------------------------------------

type Line = { role: string; role_label?: string; zone: string | null; expected: number; present: number; late: number; absent: number };
type Taken = { id: string; zone: string | null; taken_at: string; counts: Line[] };

export function Headcount({ eventId }: { eventId: string }) {
  const { apiBase } = useSession();
  const { t, refusal } = useT();
  const [lines, setLines] = useState<Line[] | null>(null);
  const [taken, setTaken] = useState<Taken[]>([]);
  const [zone, setZone] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  // The earlier roll calls are a second read; when it fails the list says so rather than
  // reading as "none taken yet".
  const [pastFailed, setPastFailed] = useState(false);

  const load = useCallback(async () => {
    try {
      const [live, past] = await Promise.all([
        send<{ roll_call?: { counts: Line[] }; error?: { message?: string } }>(console_.rollCallTally(apiBase, eventId)),
        send<{ roll_calls?: Taken[] }>(console_.rollCalls(apiBase, eventId)),
      ]);
      if (live.status !== 200 || !live.body.roll_call) {
        setNotice(refusal(live.body.error) ?? t("This crew could not be read."));
        return;
      }
      setLines(live.body.roll_call.counts);
      setTaken(past.body.roll_calls ?? []);
      setPastFailed(past.status !== 200);
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    }
  }, [apiBase, eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  async function takeRollCall() {
    setBusy(true);
    setNotice(null);
    try {
      const res = await send<{ error?: { message?: string } }>(
        console_.takeRollCall(apiBase, eventId, zone.trim() ? { zone: zone.trim() } : {}),
      );
      if (res.status !== 200) {
        setNotice(refusal(res.body.error) ?? t("That could not be recorded."));
        return;
      }
      await load();
    } catch {
      setNotice(t("We could not reach Verbunk. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  }

  const zones = [...new Set((lines ?? []).map((l) => l.zone).filter((z): z is string => z !== null))];

  return (
    <section className="panel">
      <div className="panel-head">
        <h2>{t("Headcount")}</h2>
        <p className="muted">{t("Counts per role and zone. Only counts leave Verbunk; names stay here.")}</p>
      </div>
      {lines === null ? (
        notice ? <StatusChip tone="failed" label={notice} /> : <p className="muted">{t("Reading the crew…")}</p>
      ) : (
        <>
          {lines.length === 0 ? (
            <p className="muted">{t("Nobody is expected at these posts right now.")}</p>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t("Role")}</th>
                  <th>{t("Zone")}</th>
                  <th>{t("Expected")}</th>
                  <th>{t("Present")}</th>
                  <th>{t("Late")}</th>
                  <th>{t("Absent")}</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={`${l.role}-${l.zone ?? ""}`}>
                    <td>{l.role_label ?? l.role}</td>
                    <td>{l.zone ?? "—"}</td>
                    <td className="numeric">{l.expected}</td>
                    <td className="numeric">{l.present}</td>
                    <td className="numeric">{l.late}</td>
                    <td className="numeric">{l.absent}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <div className="row">
            <label>
              {t("Zone")}{" "}
              <select value={zone} onChange={(e) => setZone(e.target.value)}>
                <option value="">{t("All zones")}</option>
                {zones.map((z) => (
                  <option key={z} value={z}>{z}</option>
                ))}
              </select>
            </label>
            <button type="button" className="btn" disabled={busy || lines.length === 0} onClick={() => void takeRollCall()}>
              {busy ? t("Recording…") : t("Take roll call")}
            </button>
          </div>
          {notice ? (
            <div className="form-message" role="status" aria-live="polite">
              <StatusChip tone="attention" label={t("Not recorded")} />
              <span>{notice}</span>
            </div>
          ) : null}
          {pastFailed ? <p className="muted">{t("Earlier roll calls could not be read.")}</p> : null}
          {taken.length > 0 ? (
            <ul className="plain-list">
              {taken.map((r) => (
                <li key={r.id}>
                  <strong>{clock(r.taken_at)}</strong> {r.zone ?? t("All zones")} ·{" "}
                  {t("{present} of {expected} present", {
                    present: r.counts.reduce((n, l) => n + l.present, 0),
                    expected: r.counts.reduce((n, l) => n + l.expected, 0),
                  })}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      )}
    </section>
  );
}
